import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AppStateFailureBuilder, AppStateFailureItemWithErrorCode, FailureType } from '@symphony-solo/shared/common-state/src';
import { ApiResponseCode } from '@symphony-solo/shared/http/src';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { LoyaltyCardsService } from '../services';
import {
  addLoyaltyCardsAction,
  addLoyaltyCardsErrorAction,
  addLoyaltyCardsSuccessAction,
  deleteLoyaltyCardAction,
  deleteLoyaltyCardErrorAction,
  deleteLoyaltyCardSuccessAction,
  getAppleWalletPassAction,
  getAppleWalletPassSuccessAction,
  getGoogleWalletPassAction,
  getGoogleWalletPassErrorAction,
  getGoogleWalletPassSuccessAction,
  loadLoyaltyCardsAction,
  loadLoyaltyCardsErrorAction,
  loadLoyaltyCardsSuccessAction,
} from './loyalty-cards.actions';
import { LoyaltyCardsFacade } from './loyalty-cards.facade';
import { AlertService } from '@symphony-solo/shared/alert/src/lib/services/alert.service';
// const { Filesystem, Browser } = Plugins;

@Injectable()
export class LoyaltyCardsEffects {
  constructor(
    private action$: Actions,
    private loyaltyCardsService: LoyaltyCardsService,
    private loyaltyCardFacade: LoyaltyCardsFacade,
    private alertService: AlertService
  ) {}

  protected loadLoyaltyCards$ = createEffect(() =>
    this.action$.pipe(
      ofType(loadLoyaltyCardsAction),
      switchMap(() => {
        return this.loyaltyCardsService.getLoyaltyCards().pipe(
          map((loyaltyCards) => {
            return loadLoyaltyCardsSuccessAction({ loyaltyCards });
          }),
          catchError(() => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Error on loading Loyalty Cards', ApiResponseCode.Server_Error))
              .build();
            return of(loadLoyaltyCardsErrorAction({ failure }));
          })
        );
      })
    )
  );

  protected addLoyaltyCards$ = createEffect(() =>
    this.action$.pipe(
      ofType(addLoyaltyCardsAction),
      switchMap((CardID) => {
        return this.loyaltyCardsService.addLoyaltyCard(CardID.loyaltyCardID).pipe(
          map(() => {
            return addLoyaltyCardsSuccessAction();
          }),
          catchError((error) => {
            return of(addLoyaltyCardsErrorAction({ failure: error }));
          })
        );
      })
    )
  );

  protected addLoyaltyCardsError$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(addLoyaltyCardsErrorAction),
        tap((error) => {
          const okButton = {
            text: 'OK',
            cssClass: 'okButton centerButton',
          };
          this.alertService.presentAlert('Failure', error.failure['error'], [okButton]);
        })
      ),
    {
      dispatch: false,
    }
  );

  protected addLoyaltyCardsSuccess$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(addLoyaltyCardsSuccessAction),
        tap(() => {
          this.loyaltyCardFacade.loadLoyaltyCards();
          this.alertService.presentCustomAlert('Success', 'Card saved successfully', 'Success');
        })
      ),
    {
      dispatch: false,
    }
  );

  protected deleteLoyaltyCard$ = createEffect(() =>
    this.action$.pipe(
      ofType(deleteLoyaltyCardAction),
      switchMap((LoyaltyCardID) => {
        return this.loyaltyCardsService.deleteLoyaltyCard(LoyaltyCardID.loyaltyCardID).pipe(
          map(() => {
            return deleteLoyaltyCardSuccessAction();
          }),
          catchError(() => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Error on deleting Loyalty card', ApiResponseCode.Server_Error))
              .build();
            return of(deleteLoyaltyCardErrorAction({ failure }));
          })
        );
      })
    )
  );

  protected deleteLoyaltyCardsSuccess$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(deleteLoyaltyCardSuccessAction),
        tap(() => {
          this.loyaltyCardFacade.loadLoyaltyCards();
          // const okButton = {
          //   text: 'OK',
          //   cssClass: 'okButton centerButton',
          // };
          // this.alertService.presentAlert('Success', 'Your loyalty card have been deleted successfully.', [okButton])
        })
      ),
    {
      dispatch: false,
    }
  );

  protected getGoogleWalletPass$ = createEffect(() =>
    this.action$.pipe(
      ofType(getGoogleWalletPassAction),
      switchMap(() => {
        return this.loyaltyCardsService.getGoogleWalletPass().pipe(
          map((getGoogleWalletPass) => {
            return getGoogleWalletPassSuccessAction({ getGoogleWalletPass });
          }),
          catchError(() => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Error loading Google Wallet', ApiResponseCode.Server_Error))
              .build();
            return of(getGoogleWalletPassErrorAction({ failure }));
          })
        );
      })
    )
  );

  protected getAppleWalletPass$ = createEffect(() =>
    this.action$.pipe(
      ofType(getAppleWalletPassAction),
      switchMap(() => {
        return this.loyaltyCardsService.getAppleWalletPass().pipe(
          map((appleWalletPass) => {
            return getAppleWalletPassSuccessAction({ appleWalletPass });
          }),
          catchError(() => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Error loading Apple Wallet', ApiResponseCode.Server_Error))
              .build();
            return of(getGoogleWalletPassErrorAction({ failure }));
          })
        );
      })
    )
  );
}
